import FanLoadingIcon from 'components/FanLoadingIcon';
import RangeSlider from 'components/RangeSlider';
import Switch from 'components/Switch';
import { SENSOR_MAX_LAT, SENSOR_MAX_LNG, SENSOR_MIN_LAT, SENSOR_MIN_LNG } from 'constants/config';
import { DIALOG_ERROR_CONFIRM_TIME_LESS_THAN_SCAN_TIME, DIALOG_ERROR_INVALID_CONFIRM_TIME, DIALOG_ERROR_INVALID_LAT, DIALOG_ERROR_INVALID_LNG, DIALOG_ERROR_INVALID_SCAN_TIME, TEXT_BUTTON_UPDATE, TEXT_LATITUDE, TEXT_LONGITUDE, TEXT_ZOOM_RATE } from 'constants/message';
import { useDialog } from 'context/DialogContext';
import PropTypes from 'prop-types';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDebouncedCallback } from 'use-debounce';
import "./ModifyGeneralSettingForm.css";

const SelectSearchProvinces = ({ defaultValue, setSelectedBoundingBox }) => {
  const [listBoundingBox, setListBoundingBox] = React.useState([])
  const [text, setText] = React.useState(defaultValue);
  const [loading, setLoading] = React.useState(false);

  const searchBoundingBox = async (value) => {
    try {
      setLoading(true)
      const res = await fetch(`https://nominatim.openstreetmap.org/search?q=${value}&polygon_geojson=1&countrycodes=jp&format=json`)
      const data = await res.json()
      setListBoundingBox(data)
      setLoading(false)
    } catch (error) {
      setLoading(false)
    }
  }
  const onSelectBoundingBox = (box) => {
    setSelectedBoundingBox(box)
    setText(box.display_name)
    setListBoundingBox([])
  }
  const debounced = useDebouncedCallback(
    async (value) => {
      if (!value) {
        setListBoundingBox([])
        setSelectedBoundingBox(undefined)
        return
      }
      await searchBoundingBox(value.trim())
    },
    700
  );

  return <div className='select_search_provinces'>
    <input
      type='text'
      style={{ textAlign: 'right' }}
      value={text}
      onChange={(e) => {
        setText(e.target.value)
        debounced(e.target.value)
      }}
    />
    {loading ? (<div className='select_search_provinces_loading'><FanLoadingIcon size={16} strokeWidth={2} /></div>) : (
      listBoundingBox.length > 0 ?
        <div className='select_search_provinces_list'>
          {listBoundingBox.map((item) => (
            <div onClick={() => onSelectBoundingBox(item)} className='select_search_provinces_item' key={item.place_id}>{item.display_name}</div>
          ))}
        </div> : ''
    )}
  </div>
}

const propTypes = {
  initSetting: PropTypes.object,
  onSubmit: PropTypes.func
};
const defaultProps = {
  initSetting: {},
  onSubmit: (data) => { }
};
const ModifyGeneralSettingForm = (props) => {
  /*** States and Variables ***/
  const { initSetting } = props;
  const {
    register, handleSubmit, control, watch, reset
  } = useForm();
  const { error } = useDialog();
  const [boundingBox, setSelectedBoundingBox] = React.useState(initSetting.boundingBox)

  /*** Processing ***/
  React.useEffect(() => {
    reset({
      scanTime: initSetting.scanTime || 3,
      confirmTime: initSetting.confirmTime || 30,
      appTitle: initSetting.appTitle || '冠水監視',
      apiAuth: initSetting.apiAuth || '',
      alertOn: !!initSetting.alertOn,
      zoomOnClick: !!initSetting.zoomOnClick,
      statusHoldTimes: initSetting.statusHoldTimes || 1,
      lat: initSetting.lat || 36.83763081714207,
      lng: initSetting.lng || 139.3154172350928,
      zoom: initSetting.zoom || 10
    });
  }, [initSetting])

  /*** Sub Components ***/

  /*** Event Handlers ***/
  const onSubmit = (data) => {
    const parsedScanTime = parseInt(data.scanTime);
    const parsedConfirmTime = parseInt(data.confirmTime);
    data.boundingBox = boundingBox
    if (!isFinite(parsedScanTime) || isNaN(parsedScanTime) || parsedScanTime < 1 || parsedScanTime > 60) {
      error(DIALOG_ERROR_INVALID_SCAN_TIME);
      return;
    }
    if (!isFinite(parsedConfirmTime) || isNaN(parsedConfirmTime) || parsedConfirmTime < 1 || parsedConfirmTime > 1440) {
      error(DIALOG_ERROR_INVALID_CONFIRM_TIME);
      return;
    }
    if (parsedConfirmTime < parsedScanTime) {
      error(DIALOG_ERROR_CONFIRM_TIME_LESS_THAN_SCAN_TIME);
      return;
    }
    data.scanTime = parsedScanTime;
    data.confirmTime = parsedConfirmTime;
    const parsedLat = parseFloat(data.lat);
    const parsedLng = parseFloat(data.lng);
    if (isNaN(data.lat) || !isFinite(parsedLat) || isNaN(parsedLat) || parsedLat < SENSOR_MIN_LAT || parsedLat > SENSOR_MAX_LAT) {
      error(DIALOG_ERROR_INVALID_LAT);
      return;
    }
    if (isNaN(data.lng) || !isFinite(parsedLng) || isNaN(parsedLng) || parsedLng < SENSOR_MIN_LNG || parsedLng > SENSOR_MAX_LNG) {
      error(DIALOG_ERROR_INVALID_LNG);
      return;
    }
    data.lat = parsedLat;
    data.lng = parsedLng;
    props.onSubmit(data);
  }
  /*** Main Render ***/
  return <form
    onSubmit={handleSubmit(onSubmit)}
    className='modify_general_setting_form'>
    <div className='modify_general_setting_form_item_row'>
      <label className='modify_general_setting_form_item_key'>
        {`データ取得API実行間隔（分）`}
      </label>
      <div className='modify_general_setting_form_item_value'>
        <input
          type="number"
          min={1}
          max={60}
          step={1}
          style={{ textAlign: 'right' }}
          {...register('scanTime')} />
      </div>
    </div>
    <div className='modify_general_setting_form_item_row'>
      <label className='modify_general_setting_form_item_key'>
        {`異常検知時間 (分)`}
      </label>
      <div className='modify_general_setting_form_item_value'>
        <input
          type="number"
          min={1}
          max={1440}
          step={1}
          style={{ textAlign: 'right' }}
          {...register('confirmTime')} />
      </div>
    </div>
    <div className='modify_general_setting_form_item_row'>
      <label className='modify_general_setting_form_item_key'>
        アプリタイトル
      </label>
      <div className='modify_general_setting_form_item_value'>
        <input
          type="text"
          {...register('appTitle')} />
      </div>
    </div>
    <div className='modify_general_setting_form_item_row'>
      <label className='modify_general_setting_form_item_key'>
        APIキー
      </label>
      <div className='modify_general_setting_form_item_value'>
        <input
          type="password"
          {...register('apiAuth')} />
      </div>
    </div>
    <div className='modify_general_setting_form_item_row' >
      <label className='modify_general_setting_form_item_key'>
        警報の保持・復旧
      </label>
      <div className='modify_general_setting_form_item_value'>
        <Controller
          control={control}
          name='alertOn'
          render={({ field }) => {
            return <Switch {...field} />
          }}
        />
      </div>
    </div>
    <div className='modify_general_setting_form_item_row'>
      <label className='modify_general_setting_form_item_key'>
        ステータス決定までのAPI実行回数
      </label>
      <div className='modify_general_setting_form_item_value'>
        <input
          type='number'
          step={1}
          style={{ textAlign: 'right' }}
          min={1}
          max={10}
          {...register('statusHoldTimes')} />
      </div>
    </div>
    <div style={{ width: '600px', border: 'solid 1px #707070', margin: '10px 0' }} />
    <div className='modify_general_setting_form_item_row'>
      センサマップ画面初期表示設定
    </div>
    <div className='modify_general_setting_form_item_row'>
      <label className='modify_general_setting_form_item_key'>
        {TEXT_LATITUDE}
      </label>
      <div className='modify_general_setting_form_item_value'>
        <input
          type='text'
          style={{ textAlign: 'right' }}
          {...register('lat')} />
      </div>
    </div>
    <div className='modify_general_setting_form_item_row'>
      <label className='modify_general_setting_form_item_key'>
        {TEXT_LONGITUDE}
      </label>
      <div className='modify_general_setting_form_item_value'>
        <input
          type='text'
          style={{ textAlign: 'right' }}
          {...register('lng')} />
      </div>
    </div>
    <div className='modify_general_setting_form_item_row'>
      <label className='modify_general_setting_form_item_key'>
        {`${TEXT_ZOOM_RATE}(${watch('zoom') || 10})`}
      </label>
      <div className='modify_general_setting_form_item_value'>
        <Controller
          name='zoom'
          control={control}
          render={({ field }) => {
            return <RangeSlider
              min={1}
              max={18}
              step={0.5}
              progress={((field.value ?? 1) - 1) / (18 - 1)}
              {...field}
              value={field.value}
            />
          }}
        />
      </div>
    </div>
    <div style={{ width: '600px', border: 'solid 1px #707070', margin: '0 0 20px' }} />
    <div className='modify_general_setting_form_item_row'>
      マップ画面設定
    </div>
    <div className='modify_general_setting_form_item_row'>
      <label className='modify_general_setting_form_item_key'>
        アイコンクリック時のズーム
      </label>
      <div className='modify_general_setting_form_item_value'>
        <Controller
          control={control}
          name='zoomOnClick'
          render={({ field }) => {
            return <Switch {...field} />
          }}
        />
      </div>
    </div>
    <div className='modify_general_setting_form_item_row'>
      <label className='modify_general_setting_form_item_key'>
        境界線を表示する自治体名称
      </label>
      <div className='modify_general_setting_form_item_value'>
        <SelectSearchProvinces defaultValue={initSetting?.boundingBox?.display_name} setSelectedBoundingBox={setSelectedBoundingBox} />
      </div>
    </div>
    <div style={{ width: '600px', border: 'solid 1px #707070', margin: '0 0 20px' }} />
    <div className='modify_general_setting_form_item_row'>
      <button
        className='button_active'>
        {TEXT_BUTTON_UPDATE}
      </button>
    </div>
  </form>;
}
ModifyGeneralSettingForm.propTypes = propTypes;
ModifyGeneralSettingForm.defaultProps = defaultProps;
export default ModifyGeneralSettingForm;